import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { StarRating } from '@/components/StarRating';
import { AuthorHeader } from '@/components/AuthorHeader';
import { ExternalShareButton } from '@/components/ExternalShareButton';
import { CoverImage } from '@/components/CoverImage';
import { LikeButton } from '@/components/LikeButton';
import { CommentButton } from '@/components/CommentButton';
import { SaveToTBRButton } from '@/components/SaveToTBRButton';
import { ReportButton } from '@/components/ReportButton';
import { CommentThread } from '@/components/CommentThread';
import { useLikes } from '@/hooks/useLikes';
import { useComments } from '@/hooks/useComments';
import { useSaveToTBR } from '@/hooks/useSaveToTBR';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface ReviewCardProps {
  review: {
    id: string;
    book_id: string;
    rating_star: number;
    review_text?: string;
    created_at: string;
    user_id: string;
    books?: {
      id: string;
      title: string;
      authors: string[];
      cover_url?: string;
    };
    profiles?: {
      id: string;
      name?: string;
      username?: string;
      image?: string;
      is_private_name?: boolean;
    };
  };
  showBookInfo?: boolean;
}

export function ReviewCard({ review, showBookInfo = true }: ReviewCardProps) {
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  
  const { likes, likeCount, isLiked, toggleLike, loading: likesLoading } = useLikes(review.id);
  const { commentCount } = useComments(review.id);
  const { isSaved, toggleSave, loading: saveLoading } = useSaveToTBR(review.book_id, review.id);

  const handleBookClick = () => {
    if (review.books) {
      navigate(`/book/${review.books.id}`);
    }
  };
  return (
    <Card className="w-full card-elevated">
      <CardContent className="px-5 py-4">
        <div className="flex gap-4">
          {/* Book cover (if showing book info) */}
          {showBookInfo && review.books && (
            <div className="flex-shrink-0">
              <CoverImage
                bookId={review.books.id}
                title={review.books.title}
                author={review.books.authors?.[0]}
                coverUrl={review.books.cover_url}
                size="sm"
                onClick={handleBookClick}
              />
            </div>
          )}

          <div className="flex-1 space-y-2">
            {/* User info row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={review.profiles?.image} />
                  <AvatarFallback className="text-xs">
                    {review.profiles?.name?.charAt(0) || review.profiles?.username?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <AuthorHeader
                  username={review.profiles?.username}
                  name={review.profiles?.name}
                  showFullFormat={true}
                />
              </div>
              <time className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
              </time>
            </div>

            {/* Book title and author */}
            {showBookInfo && review.books && (
              <div className="space-y-0.5">
                <button
                  onClick={handleBookClick}
                  className="text-left hover:text-primary transition-colors cursor-pointer"
                >
                  <h3 className="font-semibold text-sm leading-tight hover:underline">{review.books.title}</h3>
                </button>
                <p className="text-xs text-muted-foreground">
                  {review.books.authors?.map((author, index) => (
                    <React.Fragment key={author}>
                      <a 
                        href={`/author/${encodeURIComponent(author)}`}
                        className="hover:text-primary hover:underline transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          navigate(`/author/${encodeURIComponent(author)}`);
                        }}
                      >
                        {author}
                      </a>
                      {index < (review.books.authors?.length || 0) - 1 && ', '}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            )}

            {/* Rating */}
            <div className="flex items-center gap-1.5">
              <StarRating value={review.rating_star} readonly size="sm" />
              <span className="text-xs text-muted-foreground">{review.rating_star.toFixed(1)}</span>
            </div>

            {/* Review text */}
            {review.review_text && (
              <p className="text-sm leading-relaxed">
                {review.review_text}
              </p>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <LikeButton
                  isLiked={isLiked}
                  likeCount={likeCount}
                  onToggle={toggleLike}
                  loading={likesLoading}
                />
                
                <CommentButton
                  commentCount={commentCount}
                  onClick={() => setShowComments(true)}
                />
                
                <ExternalShareButton
                  shareType="review"
                  data={{
                    bookTitle: review.books?.title,
                    rating: review.rating_star,
                    bookId: review.book_id,
                    reviewId: review.id,
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                />

                <ReportButton
                  contentType="review"
                  contentId={review.id}
                  contentPreview={review.review_text}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                />
              </div>

              {/* Save to TBR button - only show if this isn't the current user's review */}
              {showBookInfo && review.books && (
                <SaveToTBRButton
                  isSaved={isSaved}
                  onToggle={toggleSave}
                  loading={saveLoading}
                  size="sm"
                  variant="outline"
                />
              )}
            </div>
          </div>
        </div>

        {/* Comment Thread Modal */}
        {showComments && review.books && (
          <CommentThread
            isOpen={showComments}
            onClose={() => setShowComments(false)}
            reviewId={review.id}
            bookTitle={review.books.title}
          />
        )}
      </CardContent>
    </Card>
  );
}